import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import moment from 'moment';
import React, { useMemo } from 'react';
import { SelectOption } from '../../interfaces/common';
import { StyledTheme } from '../../styles/styleTheme';
import { CustomModal } from '../common/CustomModal/CustomModal';

interface SyncClaimsDrawerBodyProps {
  clinic: string[];
  clinics: SelectOption[];
  fromSyncDate: string;
  untilSyncDate: string;
  forceEhrFetch: boolean;
  syncLoading: boolean;
  lastSyncedAt: Date | null;
  onClinicChange: (event: SelectChangeEvent<string[]>) => void;
  onFromSyncDateChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onUntilSyncDateChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onForceEhrFetch: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onConfirmSync: () => void;
}

const MAX_RANGE_DAYS = 7;

export const SyncClaimsDrawer: React.FC<SyncClaimsDrawerBodyProps> = ({
  clinic,
  clinics,
  fromSyncDate,
  untilSyncDate,
  forceEhrFetch,
  syncLoading,
  lastSyncedAt,
  onClinicChange,
  onFromSyncDateChange,
  onUntilSyncDateChange,
  onForceEhrFetch,
  onConfirmSync,
}) => {
  const validation = useMemo(() => {
    if (clinic.length === 0) {
      return 'Select at least one clinic.';
    }
    if (!fromSyncDate || !untilSyncDate) {
      return 'Set both From and Until dates.';
    }
    const from = moment(fromSyncDate, 'YYYY-MM-DD');
    const until = moment(untilSyncDate, 'YYYY-MM-DD');
    if (until.isBefore(from)) {
      return '"Until" must be on or after "From".';
    }
    if (until.diff(from, 'days') > MAX_RANGE_DAYS) {
      return `Maximum range is ${MAX_RANGE_DAYS} days.`;
    }
    return null;
  }, [clinic, fromSyncDate, untilSyncDate]);

  const selectedClinicLabels = clinics
    .filter((c) => clinic.includes(c.value))
    .map((c) => c.label);

  return (
    <Stack spacing={3}>
      <Typography variant='body2' color='text.secondary'>
        Pull the latest claims from the EHR for the selected range and clinics. This may take a
        few minutes.
      </Typography>

      <FormControl fullWidth variant='outlined'>
        <InputLabel>Clinics</InputLabel>
        <Select
          value={clinic}
          onChange={onClinicChange}
          label='Clinics'
          multiple
          renderValue={(selected) => selected.join(', ')}>
          {clinics.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              <Checkbox
                checked={clinic.includes(option.value)}
                style={{ padding: '0 8px' }}
                size='small'
              />
              <ListItemText
                primary={option.label}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box display='flex' gap={2}>
        <TextField
          label='From'
          type='date'
          value={fromSyncDate}
          onChange={onFromSyncDateChange}
          InputLabelProps={{ shrink: true }}
          fullWidth
          variant='outlined'
        />
        <TextField
          label='Until'
          type='date'
          value={untilSyncDate}
          onChange={onUntilSyncDateChange}
          InputLabelProps={{ shrink: true }}
          fullWidth
          variant='outlined'
        />
      </Box>

      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              size='small'
              style={{ color: StyledTheme.tealDark }}
              checked={forceEhrFetch}
              onChange={onForceEhrFetch}
            />
          }
          label='Force EHR fetch (skip cache)'
        />
      </FormGroup>

      {validation && <Alert severity='warning'>{validation}</Alert>}

      <CustomModal
        showChildrenDefault={false}
        modalProps={{
          style: { width: '480px', maxWidth: '90vw', height: 'auto', padding: '24px' },
        }}
        buttonProps={{
          buttonText: syncLoading ? 'Syncing…' : 'Sync now',
          disabled: syncLoading || !!validation,
          fullWidth: true,
          size: 'large',
          startIcon: syncLoading ? (
            <CircularProgress style={{ color: '#ffffff' }} size={20} />
          ) : undefined,
          sx: {
            '&.custom-modal-button': {
              backgroundColor: StyledTheme.tealDark,
              color: StyledTheme.white,
              textTransform: 'capitalize',
              '&:hover': { backgroundColor: StyledTheme.tealDark },
              '&.Mui-disabled': { opacity: 0.6 },
            },
          },
        }}
        actionButtons={{
          cancelButtonProps: { buttonText: 'Cancel' },
          continueButtonProps: {
            buttonText: 'Confirm sync',
            sx: { backgroundColor: StyledTheme.tealDark, color: StyledTheme.white },
          },
        }}
        onContinue={onConfirmSync}>
        <Box p={2}>
          <Typography variant='h6' gutterBottom>
            Sync claims?
          </Typography>
          <Typography variant='body2' gutterBottom>
            <strong>Range:</strong> {fromSyncDate} → {untilSyncDate}
          </Typography>
          <Typography variant='body2' gutterBottom>
            <strong>Clinics:</strong>{' '}
            {selectedClinicLabels.length ? selectedClinicLabels.join(', ') : 'None'}
          </Typography>
          {forceEhrFetch && (
            <Typography variant='body2' color='warning.main' sx={{ mt: 1 }}>
              Force EHR fetch is on — this will bypass the cache and may take longer.
            </Typography>
          )}
          <Typography variant='body2' sx={{ mt: 2 }}>
            This may take several minutes. The calendar will refresh once the sync request is
            queued.
          </Typography>
        </Box>
      </CustomModal>

      {lastSyncedAt && (
        <Typography variant='caption' color='text.secondary'>
          Last sync: {moment(lastSyncedAt).fromNow()} ({moment(lastSyncedAt).format('LLL')})
        </Typography>
      )}
    </Stack>
  );
};
